"use client";
import { useState, useEffect } from "react";
import { upperFirst } from "lodash";
import { Dropdown } from "components/UI/dropdowns/Dropdown";
import cn from "classnames";
import styles from "components/UI/dropdowns/Dropdown.module.scss";
import chevronDownIcon from "public/img/icons/arrow-down.svg";
import { SpriteIcon } from "components/UI/SpriteIcon";
import { Option } from "components/UI/form/Select";
import closeIcon from "public/img/icons/close.svg";

export type SelectButtonProps = {
  error?: boolean;
  onChange: Function;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  placeholder?: string;
  items?: Option[];
  value?: number | string | string[] | null;
  setValue?: Function;
};

const ButtonComponent = ({
  onClick,
  error,
  items,
  value,
  placeholder,
  setValue,
  onChange,
}: SelectButtonProps) => {
  if (!items) return null;

  const isMultiple = Array.isArray(value);

  const valueExists =
    ![null, undefined].includes(value as any) && (typeof value === "string" ? !!value.trim() : true);

  const displayValue = isMultiple
    ? value.length
      ? items.find((item) => item.value == value[0])?.name
      : placeholder
    : valueExists
    ? items.find((item) => item.value === value)?.name
    : placeholder;

  const removeFirstValue = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    if (!isMultiple) return;
    setValue && setValue(value.slice(1));
    onChange(value.slice(1));
  };

  return (
    <button
      type="button"
      className={cn(styles.toggleBtn, styles.toggleBtnSelect, {
        [styles.toggleBtnError]: error,
        [styles.toggleBtnPlaceholder]: isMultiple ? !value.length : !valueExists,
      })}
      onClick={onClick}
    >
      {isMultiple && value.length ? (
        <span className={styles.singleTag}>
          <span className={cn(styles.singleTagInner, { [styles.singleTagInnerWide]: value.length === 1 })}>
            {displayValue}
            <span className={styles.closeSingleTagInner} onClick={removeFirstValue}>
              <SpriteIcon icon={closeIcon} />
            </span>
          </span>
          {value.length > 1 && <span className={styles.tagsLeft}>+{value.length - 1}...</span>}
        </span>
      ) : (
        displayValue
      )}
      <SpriteIcon icon={chevronDownIcon} className={styles.toggleBtnIcon} />
    </button>
  );
};

export type SelectItemProps = {
  item?: Option;
  onClick: Function;
  value?: number | string | (string | number)[] | null;
  setValue: Function;
};

const ItemComponent = ({ item, onClick, value, setValue }: SelectItemProps) => {
  if (!item) return null;

  const isMultiple = Array.isArray(value);

  return (
    <button
      type="button"
      className={cn(styles.link, {
        [styles.linkSelected]: isMultiple ? value.includes(item.value) : value == item.value,
      })}
      dangerouslySetInnerHTML={{ __html: item.name }}
      onClick={() => {
        onClick && onClick();
        if (isMultiple) {
          setValue(
            value.includes(item.value)
              ? value.filter((itemValue) => itemValue != item.value)
              : [...value, item.value]
          );
        } else {
          setValue(item.value);
        }
      }}
    />
  );
};

export type DropdownSelectProps = {
  items: Option[];
  className?: string;
  placeholder?: string;
  error?: boolean;
  onChange: Function;
  mode?: "single" | "multiple";
  initialValue?: number | string | string[] | null;
};

export const DropdownSelect = ({
  items,
  initialValue,
  className,
  error,
  onChange,
  placeholder,
  mode = "single",
}: DropdownSelectProps) => {
  const [value, setValue] = useState<number | string | string[] | undefined | null>(initialValue);

  useEffect(() => {
    setValue(initialValue);
  }, [initialValue, setValue]);

  const onValueChange = (newValue: string | string[]) => {
    onChange(newValue);
  };

  const onClick = (closeDropdown: Function) => {
    if (mode === "single") closeDropdown();
  };

  return (
    <Dropdown
      className={cn(
        styles.dropdownResponsiveSelect,
        styles[`mode${upperFirst(mode)}`],
        {
          [styles.dropdownResponsiveError]: error,
        },
        className
      )}
      items={items}
      ButtonComponent={ButtonComponent}
      ItemComponent={ItemComponent}
      buttonProps={{ error, value, placeholder, setValue, onChange }}
      itemProps={{ value, setValue: onValueChange, onClick }}
    />
  );
};
